/* eslint-disable */
// @ts-nocheck

import { ChevronDown, Search } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { getProducts, ProductsParams } from '@/api/productsAPI'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../../../components/ui/form'

export interface ISelectFormData {
  id: string | number
  title: string
  subtitle?: string
}

interface Props {
  name: string
  label?: string
  data: ISelectFormData[]
}

const SelectFormProducts = React.forwardRef<
  HTMLButtonElement,
  Props & UseControllerProps
>((props, ref) => {
  const { control, name, label, data } = props
  const [products, setProducts] = React.useState(data)
  const { fieldState, field } = useController(props)
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
  const [selected, setSelected] = React.useState<ISelectFormData | undefined>(
    undefined
  )
  const [searchQuery, setSearchQuery] = React.useState('')

  // Use queryClient for caching
  const queryClient = useQueryClient()

  // Actualizar selected cuando cambie el field.value o products
  React.useEffect(() => {
    if (field.value && Array.isArray(field.value) && field.value.length > 1) {
      // field.value is [title, id]
      const productId = field.value[1]
      const foundProduct = products.find((item) => item.id == productId) // Use == to handle string/number comparison
      if (foundProduct && foundProduct !== selected) {
        setSelected(foundProduct)
      }
    } else if (
      !field.value ||
      (Array.isArray(field.value) && field.value.length === 0)
    ) {
      setSelected(undefined)
    }
  }, [field.value, products])

  // Sincronizar products cuando cambian los datos iniciales
  React.useEffect(() => {
    setProducts(data)
  }, [data])

  React.useEffect(() => {
    setProducts(data)
  }, [data])

  React.useEffect(() => {
    console.log('Field value:', field.value) // Debug
    console.log('Products:', products) // Debug
    console.log('Products length:', products.length) // Debug

    if (
      field.value &&
      Array.isArray(field.value) &&
      field.value.length >= 2 &&
      products.length > 0
    ) {
      const targetId = field.value[1]
      const targetTitle = field.value[0]
      console.log(
        'Looking for product with ID:',
        targetId,
        'Type:',
        typeof targetId
      ) // Debug

      // Mostrar todos los IDs disponibles
      console.log(
        'Available product IDs:',
        products.map((p) => ({ id: p.id, type: typeof p.id, title: p.title }))
      )

      const foundProduct = products.find((itm) => itm.id == targetId)

      console.log('Found product:', foundProduct) // Debug

      if (foundProduct) {
        console.log('Setting selected product:', foundProduct) // Debug
        setSelected(foundProduct)
      } else {
        console.log(
          'Product not found in current list, creating temporary product object'
        ) // Debug
        // If product is not in current list, create temporary object
        const tempProduct = {
          id: targetId,
          title: targetTitle,
          subtitle: undefined,
        }
        console.log('Setting temporary selected product:', tempProduct) // Debug
        setSelected(tempProduct)

        setProducts((prev) => {
          // Check if already in list to avoid duplicates
          const exists = prev.find((p) => p.id == targetId)
          if (!exists) {
            return [tempProduct, ...prev]
          }
          return prev
        })
      }
    } else {
      console.log('Clearing selection - invalid field value or no products:', {
        fieldValue: field.value,
        isArray: Array.isArray(field.value),
        length: Array.isArray(field.value) ? field.value.length : 'N/A',
        productsLength: products.length,
      })
      setSelected(null)
    }
  }, [field.value, products])

  const GetProductsData = async (search: string) => {
    if (!search.trim()) {
      // If no search, show only initial cache products
      setProducts(data)
      return
    }

    try {
      // Check cache first
      const queryKey = ['products-full', search]
      const cached = queryClient.getQueryData<
        Array<{
          id: string | number
          title: string
          subtitle?: string
        }>
      >(queryKey)

      if (cached) {
        // Combinar con datos iniciales
        const existingIds = new Set(data.map((product) => product.id))
        const uniqueNewProducts = cached.filter(
          (product) => !existingIds.has(product.id)
        )
        const combinedProducts = [...data, ...uniqueNewProducts]
        setProducts(combinedProducts)

        // Mantener producto seleccionado si existe
        if (
          field.value &&
          Array.isArray(field.value) &&
          field.value.length > 1
        ) {
          const selectedId = field.value[1]
          const foundProduct = combinedProducts.find(
            (item) => item.id === selectedId
          )
          if (foundProduct) {
            setSelected(foundProduct)
          }
        }
        return
      }

      // If not in cache, fetch from API
      const response = await getProducts({
        page: 1,
        per_page: 10,
        search: search,
        type: 'STOCK,RAW,MANUFACTURED,INPUT,ASSET,VARIATION',
      })

      const newProducts = response.map(
        ({ id, name, salesCode, description }) => ({
          id: id,
          title: salesCode ? `${name}-${salesCode}` : name,
          subtitle: description,
        })
      )

      // Save to cache
      queryClient.setQueryData(queryKey, newProducts, {
        updatedAt: Date.now(),
      })

      // Combine initial cache products with new search results
      // Avoid duplicates using Set based on ID
      const existingIds = new Set(data.map((product) => product.id))
      const uniqueNewProducts = newProducts.filter(
        (product) => !existingIds.has(product.id)
      )
      const combinedProducts = [...data, ...uniqueNewProducts]

      setProducts(combinedProducts)

      // If there was a selected product, try to maintain it after update
      if (field.value && Array.isArray(field.value) && field.value.length > 1) {
        const selectedId = field.value[1]
        const foundProduct = combinedProducts.find(
          (item) => item.id === selectedId
        )
        if (foundProduct) {
          setSelected(foundProduct)
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error)
      // En caso de error, mantener los productos del cache inicial
      setProducts(data)
    }
  }

  React.useEffect(() => {
    // Debounce search to avoid too many requests
    const timeoutId = setTimeout(() => {
      GetProductsData(searchQuery)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const filteredData = products.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleSelect = (selectedItem: ISelectFormData) => {
    console.log('Selecting product:', selectedItem) // Debug
    setIsPopoverOpen(false)
    setSearchQuery('')
    setSelected(selectedItem)
    field.onChange([selectedItem.title, selectedItem.id])

    // Asegurar que el componente padre se entere del cambio
    if (props.onChange) {
      props.onChange([selectedItem.title, selectedItem.id])
    }
  }
  return (
    <div className={'w-full'}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <Popover onOpenChange={setIsPopoverOpen} open={isPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    aria-expanded={isPopoverOpen}
                    className="h-fit w-full justify-between font-normal"
                    ref={ref}
                    role="combobox"
                    variant="outline"
                  >
                    {selected ? (
                      <div className="flex flex-col items-start">
                        <span>{selected.title}</span>
                        {selected.subtitle && (
                          <span className="text-xs text-muted-foreground">
                            {selected.subtitle}
                          </span>
                        )}
                      </div>
                    ) : (
                      'Seleccionar...'
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width]">
                  <div className="px-3 pt-3">
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        className="pl-9"
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar..."
                        value={searchQuery}
                      />
                    </div>
                  </div>
                  <div
                    className="max-h-[200px] overflow-y-auto"
                    onWheel={(e) => {
                      const target = e.currentTarget
                      target.scrollTop += e.deltaY
                    }}
                  >
                    {filteredData.length > 0 ? (
                      filteredData.map((item) => (
                        <div
                          className="cursor-pointer p-2 hover:bg-muted"
                          key={item.id}
                          onClick={() => handleSelect(item)}
                        >
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.subtitle}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        No se encontraron productos.
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </FormControl>
            <FormMessage>{fieldState.error?.message}</FormMessage>
          </FormItem>
        )}
      />
    </div>
  )
})

export { SelectFormProducts }
